import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../services/firebase';
import { useAuth } from './AuthContext';
import { useWallet } from './WalletContext';
import { earnToWalletA } from '../services/walletService';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  arrayUnion,
  increment,
  addDoc,
  serverTimestamp,
  orderBy,
  limit
} from 'firebase/firestore';

const ReferralContext = createContext();

export const useReferral = () => {
  return useContext(ReferralContext);
};

export const ReferralProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [referralCode, setReferralCode] = useState('');
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referralStats, setReferralStats] = useState({
    totalReferrals: 0,
    totalEarnings: 0
  });

  // Generate referral code based on user ID
  const generateReferralCode = (uid) => {
    // Take first 6 characters of UID and add random 4 characters
    const prefix = uid.substring(0, 6);
    const randomChars = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `${prefix}${randomChars}`;
  };

  // Initialize referral code for new user
  useEffect(() => {
    const initializeReferralSystem = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // If user already has a referral code, use it
          if (userData.referralCode) {
            setReferralCode(userData.referralCode);
          } else {
            // Generate and save new referral code
            const newCode = generateReferralCode(currentUser.uid);
            await updateDoc(userRef, { referralCode: newCode });
            setReferralCode(newCode);
          }
          
          // Fetch referrals
          await fetchReferrals();
        } else {
          // Create user doc with referral code if it doesn't exist
          const newCode = generateReferralCode(currentUser.uid);
          await setDoc(userRef, {
            referralCode: newCode,
            referrerUID: null, // Will be populated if user used a referral code
            email: currentUser.email,
            displayName: currentUser.displayName,
            username: currentUser.displayName || currentUser.email.split('@')[0],
            totalReferralEarnings: 0,
            walletA: 0,
            walletB: 0,
            // Wallet sources tracking
            walletSources: {
              ads: 0,
              referrals: 0,
              login: 0,
              accuracyBonus: 0
            }
          });
          setReferralCode(newCode);
        }
      } catch (error) {
        console.error('Error initializing referral system:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeReferralSystem();
  }, [currentUser]);

  // Apply someone else's referral code
  const applyReferralCode = async (code) => {
    if (!currentUser) return { success: false, message: 'You must be logged in' };
    if (!code) return { success: false, message: 'Please enter a referral code' };

    try {
      console.log('Applying referral code:', code);
      
      // Check if user already has a referrer
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists() && userDoc.data().referrerUID) {
        console.log('User already has a referrer:', userDoc.data().referrerUID);
        return { success: false, message: 'You already used a referral code' };
      }

      // Find user with this referral code
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('referralCode', '==', code));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          console.log('No user found with referral code:', code);
          return { success: false, message: 'Invalid referral code' };
        }

        const referrerDoc = querySnapshot.docs[0];
        const referrerUID = referrerDoc.id;
        console.log('Found referrer:', referrerUID);

        // Cannot use your own code
        if (referrerUID === currentUser.uid) {
          console.log('User tried to use their own referral code');
          return { success: false, message: 'You cannot use your own referral code' };
        }

        // Update user with referrer UID
        console.log('Updating user with referrer UID:', referrerUID);
        
        try {
          await updateDoc(userRef, {
            referrerUID: referrerUID
          });
          console.log('Successfully updated referrerUID in database');
          
          // Double-check that the update succeeded
          const updatedDoc = await getDoc(userRef);
          if (updatedDoc.exists() && updatedDoc.data().referrerUID === referrerUID) {
            console.log('Verified referrerUID was set correctly:', updatedDoc.data().referrerUID);
          } else {
            console.error('Failed to set referrerUID correctly. Current value:', 
              updatedDoc.exists() ? updatedDoc.data().referrerUID : 'document does not exist');
            // Continue anyway since we have the referrer UID and can still process the reward
          }
        } catch (updateError) {
          console.error('Error updating user with referrerUID:', updateError);
          // Continue anyway - we'll store the relation in the transactions
        }
        
        // Award 1000 coins to the referrer
        console.log('Processing referral reward...');
        try {
          const success = await processReferralReward(referrerUID);
          
          if (!success) {
            console.error('Failed to process referral reward');
            // Try direct wallet update as a fallback
            try {
              console.log('Attempting direct wallet update as fallback...');
              const REFERRAL_REWARD = 1000;
              const referrerRef = doc(db, 'users', referrerUID);
              const referrerDoc = await getDoc(referrerRef);
              
              if (referrerDoc.exists()) {
                const referrerData = referrerDoc.data();
                const currentWalletA = referrerData.walletA || 0;
                const currentReferralEarnings = referrerData.walletSources?.referrals || 0;
                
                await updateDoc(referrerRef, {
                  walletA: currentWalletA + REFERRAL_REWARD,
                  'walletSources.referrals': currentReferralEarnings + REFERRAL_REWARD,
                  totalReferralEarnings: increment(REFERRAL_REWARD)
                });
                console.log('Emergency fallback: Updated wallet directly');
                return { success: true, message: 'Referral code applied successfully' };
              }
            } catch (emergencyError) {
              console.error('Emergency fallback failed:', emergencyError);
              return { success: false, message: 'Error processing referral reward. Please try again.' };
            }
          } else {
            console.log('Referral reward processed successfully');
            
            // Add a notification for the referrer about the new referral
            try {
              const notificationRef = collection(db, 'users', referrerUID, 'notifications');
              await addDoc(notificationRef, {
                type: 'referral',
                message: 'You have a new referral!',
                timestamp: serverTimestamp(),
                read: false,
                referredUser: {
                  uid: currentUser.uid,
                  displayName: currentUser.displayName || 'New User',
                  email: currentUser.email,
                  username: currentUser.email ? currentUser.email.split('@')[0] : 'user'
                }
              });
              console.log('Added notification for referrer');
            } catch (err) {
              console.error('Error adding notification:', err);
              // Continue anyway - this is not critical
            }
            
            // Also store the referrer in user's own transactions for visibility
            try {
              const myTransactionsRef = collection(db, 'users', currentUser.uid, 'transactions');
              await addDoc(myTransactionsRef, {
                type: 'info',
                source: 'referrals',
                description: 'You were referred by another user',
                timestamp: serverTimestamp(),
                referredBy: referrerUID
              });
              console.log('Added self-transaction to track referrer');
            } catch (err) {
              console.error('Error adding self-transaction:', err);
              // Continue anyway - this is not critical
            }
          }

          return { success: true, message: 'Referral code applied successfully' };
        } catch (findError) {
          console.error('Error finding referrer:', findError);
          return { success: false, message: 'Error finding referrer. Please try again.' };
        }
      } catch (error) {
        console.error('Error applying referral code:', error);
        return { success: false, message: 'Error applying referral code' };
      }
    } catch (error) {
      console.error('Error applying referral code:', error);
      return { success: false, message: 'Error applying referral code' };
    }
  };

  // Fetch user's referrals
  const fetchReferrals = async () => {
    if (!currentUser) return;

    try {
      console.log('Fetching referrals for user:', currentUser.uid);
      
      // First check if the user has a referral code
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        console.log('User document does not exist');
        return;
      }
      
      const userData = userDoc.data();
      console.log('User data:', userData);
      
      if (!userData.referralCode) {
        console.log('User does not have a referral code, generating one');
        const newCode = generateReferralCode(currentUser.uid);
        await updateDoc(userRef, { referralCode: newCode });
        setReferralCode(newCode);
      } else {
        console.log('User has referral code:', userData.referralCode);
        setReferralCode(userData.referralCode);
      }
      
      // Get all users that have this user as referrer
      console.log('Getting all users who used this referral code');
      const allUsersRef = collection(db, 'users');
      const q = query(allUsersRef, where('referrerUID', '==', currentUser.uid));
      const referredUsersSnapshot = await getDocs(q);
      
      const referralCount = referredUsersSnapshot.size;
      console.log('Found', referralCount, 'users who used this referral code');
      
      // Get the current wallet balance from user document
      const currentWalletA = userData.walletA || 0;
      const currentWalletSources = userData.walletSources || {};
      const currentReferralEarnings = currentWalletSources.referrals || 0;
      
      // Sync up total earnings with user document
      const totalEarnings = userData.totalReferralEarnings || 0;
      const REFERRAL_REWARD = 1000;
      
      // Calculate referral milestone bonuses (1000 coins for every 5 referrals)
      const milestoneBonus = Math.floor(referralCount / 5) * REFERRAL_REWARD;
      const totalReferralRewards = (referralCount * REFERRAL_REWARD) + milestoneBonus;
      
      // Log the milestone bonus calculation
      console.log('Referral count:', referralCount);
      console.log('Milestone bonus for every 5 referrals:', milestoneBonus);
      console.log('Total expected earnings (including milestone bonuses):', totalReferralRewards);
      
      // Make sure the total earnings match the actual referral count plus milestone bonuses
      if (totalEarnings !== totalReferralRewards || currentReferralEarnings !== totalReferralRewards) {
        console.log('Fixing inconsistent earnings data. Expected:', totalReferralRewards, 'Current:', totalEarnings, 'Sources:', currentReferralEarnings);
        
        // Calculate the difference to adjust walletA
        const earningsDifference = totalReferralRewards - currentReferralEarnings;
        const newWalletA = Math.max(0, currentWalletA + earningsDifference);
        
        // Update user document with corrected values
        await updateDoc(userRef, {
          totalReferralEarnings: totalReferralRewards,
          walletA: newWalletA,
          'walletSources.referrals': totalReferralRewards,
          // Initialize other wallet sources if they don't exist
          'walletSources.ads': currentWalletSources.ads || 0,
          'walletSources.login': currentWalletSources.login || 0,
          'walletSources.accuracyBonus': currentWalletSources.accuracyBonus || 0
        });
        
        // If there's a positive difference (new milestone reached), create a transaction
        if (earningsDifference > 0) {
          try {
            const transactionsRef = collection(db, 'users', currentUser.uid, 'transactions');
            await addDoc(transactionsRef, {
              type: 'earn',
              amount: earningsDifference,
              source: 'referrals',
              description: 'Referral milestone bonus',
              timestamp: serverTimestamp()
            });
            console.log('Added milestone bonus transaction:', earningsDifference);
          } catch (txError) {
            console.error('Error creating milestone bonus transaction:', txError);
          }
        }
        
        console.log('Updated wallet and earnings data with milestone bonuses');
      }
      
      // Build list of referred users
      const referralList = [];
      referredUsersSnapshot.forEach(doc => {
        const userData = doc.data();
        referralList.push({
          uid: doc.id,
          displayName: userData.displayName || userData.username || 'Anonymous User',
          username: userData.username || '',
          email: userData.email || 'No email',
          joinDate: userData.createdAt ? 
            (userData.createdAt.toDate ? new Date(userData.createdAt.toDate()).toLocaleDateString() : 'Unknown') : 'Unknown'
        });
      });
      
      // Sort the results by date descending
      referralList.sort((a, b) => {
        if (a.joinDate !== 'Unknown' && b.joinDate !== 'Unknown') {
          return new Date(b.joinDate) - new Date(a.joinDate);
        }
        return 0;
      });
      
      // Limit to 5 most recent
      const limitedReferrals = referralList.slice(0, 5);
      
      // Update referral stats using the actual referral count
      setReferralStats({
        totalReferrals: referralCount,
        totalEarnings: totalReferralRewards
      });
      
      setReferrals(limitedReferrals);
      
    } catch (error) {
      console.error('Error fetching referrals:', error);
    }
  };

  // Process referral reward - fixed 1000 coins
  const processReferralReward = async (referrerUID) => {
    if (!currentUser) {
      console.error('No current user when processing referral reward');
      return false;
    }

    try {
      const REFERRAL_REWARD = 1000; // Fixed reward amount
      console.log('Processing referral reward for user:', referrerUID);
      
      // Get current user details for the referral transaction
      const currentUserDetails = {
        uid: currentUser.uid,
        displayName: currentUser.displayName || 'New User',
        email: currentUser.email,
        username: currentUser.email ? currentUser.email.split('@')[0] : 'user',
        joinDate: new Date().toLocaleDateString()
      };
      
      // Get referrer's current data
      const referrerRef = doc(db, 'users', referrerUID);
      const referrerDoc = await getDoc(referrerRef);
      
      if (!referrerDoc.exists()) {
        console.error('Referrer document does not exist');
        return false;
      }
      
      const referrerData = referrerDoc.data();
      const currentWalletA = referrerData.walletA || 0;
      const currentWalletSources = referrerData.walletSources || {};
      const currentReferralEarnings = currentWalletSources.referrals || 0;
      
      // Get the current number of referrals
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('referrerUID', '==', referrerUID));
      const snapshot = await getDocs(q);
      const currentReferralCount = snapshot.size;
      
      // Check if this new referral hits a milestone (every 5 referrals)
      // +1 because this referral is not yet counted in the database
      const newReferralCount = currentReferralCount + 1;
      const milestoneBonus = newReferralCount % 5 === 0 ? REFERRAL_REWARD : 0;
      const totalReward = REFERRAL_REWARD + milestoneBonus;
      
      console.log('Current referral count:', currentReferralCount);
      console.log('New referral count:', newReferralCount);
      console.log('Milestone bonus:', milestoneBonus);
      console.log('Total reward for this referral:', totalReward);
      
      // Update referrer's wallet and sources directly in user document
      console.log('Updating referrer wallet and sources');
      await updateDoc(referrerRef, {
        walletA: currentWalletA + totalReward,
        'walletSources.referrals': currentReferralEarnings + totalReward,
        'walletSources.ads': currentWalletSources.ads || 0,
        'walletSources.login': currentWalletSources.login || 0,
        'walletSources.accuracyBonus': currentWalletSources.accuracyBonus || 0,
        totalReferralEarnings: increment(totalReward)
      });
      
      console.log('Updated referrer wallet balance and sources');
      
      // Create the transaction record
      try {
        console.log('Adding referral transaction');
        const transactionsRef = collection(db, 'users', referrerUID, 'transactions');
        await addDoc(transactionsRef, {
          type: 'earn',
          amount: REFERRAL_REWARD,
          source: 'referrals',
          description: 'Referral bonus',
          timestamp: serverTimestamp(),
          referredUser: currentUserDetails
        });
        console.log('Added referral transaction with referredUser info');
        
        // If there's a milestone bonus, create a separate transaction for clarity
        if (milestoneBonus > 0) {
          await addDoc(transactionsRef, {
            type: 'earn',
            amount: milestoneBonus,
            source: 'referrals',
            description: 'Referral milestone bonus (5 referrals)',
            timestamp: serverTimestamp(),
            milestone: newReferralCount
          });
          console.log('Added milestone bonus transaction');
        }
      } catch (txError) {
        console.error('Error creating referral transaction:', txError);
        // We've already updated the wallet, so continue anyway
      }
      
      // Create a self-transaction in the current user's account too
      try {
        const myTransactionsRef = collection(db, 'users', currentUser.uid, 'transactions');
        await addDoc(myTransactionsRef, {
          type: 'referral',
          source: 'referrals',
          description: 'Used referral code',
          timestamp: serverTimestamp(),
          referrerUID: referrerUID
        });
        console.log('Added self-transaction to track being referred');
      } catch (selfTxError) {
        console.error('Error creating self-transaction:', selfTxError);
      }
      
      return true;
    } catch (error) {
      console.error('Error processing referral reward:', error);
      return false;
    }
  };

  const getReferralLink = () => {
    return `${window.location.origin}/register?ref=${referralCode}`;
  };

  const value = {
    referralCode,
    referrals,
    referralStats,
    loading,
    applyReferralCode,
    fetchReferrals,
    processReferralReward,
    getReferralLink
  };

  return (
    <ReferralContext.Provider value={value}>
      {children}
    </ReferralContext.Provider>
  );
};

export default ReferralContext;